"use client";
import React from "react";
import { useMemo } from "react";
import merge from "lodash/merge";

import { createTheme, ThemeOptions, ThemeProvider as MuiThemeProvider } from "@mui/material/styles";

// system
import { palette } from "./palette";
import { shadows } from "./shadows";
import { typography } from "./typography";
// options
import { customShadows } from "./custom-shadows";
import { componentsOverrides } from "./overrides";
import { createPresets } from "./options/presets";
import { createContrast } from "./options/contrast";
import { useWidgetSettingsContext } from "../settings";
import { GoogleFontLoader } from "../utils/GoogleFontLoader";

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export default function ThemeProvider({ children }: Props) {
  const settings = useWidgetSettingsContext();

  const presets = createPresets(settings.themeColorPresets, settings.mainColor);

  const contrast = createContrast(settings.themeContrast, settings.themeMode);

  const memoizedValue = useMemo(
    () => ({
      palette: {
        ...palette(settings.themeMode),
        ...presets.palette,
        ...contrast.palette,
      },
      customShadows: {
        ...customShadows(settings.themeMode),
        ...presets.customShadows,
      },
      direction: settings.themeDirection,
      shadows: shadows(settings.themeMode),
      shape: { borderRadius: 8 },
      typography: {
        ...typography,
        fontFamily: settings.fontFamily || typography.fontFamily,
        allVariants: {
          ...(typography as any).allVariants,
          fontWeight: settings.fontWeight ? Number(settings.fontWeight) : undefined,
        },
      },
    }),
    [
      settings.themeMode,
      settings.themeDirection,
      presets.palette,
      presets.customShadows,
      contrast.palette,
      settings.fontFamily,
      settings.fontWeight,
    ]
  );

  const theme = createTheme(memoizedValue as ThemeOptions);

  theme.components = merge(componentsOverrides(theme), contrast.components);

  return (
    <MuiThemeProvider theme={theme}>
      <GoogleFontLoader fontFamily={settings.fontFamily || ""} />
      {children}
    </MuiThemeProvider>
  );
}
